import { G } from '@mobily/ts-belt';
import { addDays, format, subDays } from 'date-fns';
import { AutocompleteInput, FileField, ReferenceInput, required, useNotify } from 'react-admin';
import { useWatch } from 'react-hook-form';

import { PostB2bWithdrawalBulkTemplateEndpoint } from '@Common/types/bo/b2b/withdrawals';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { PostBoExternalWithdrawalBulkTemplateEndpoint } from '@Common/types/bo_external/withdrawals';

import { Button, FileInput, TypographyTranslated } from '@sh4res/bo-common/components';
import { FormValues } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/utils';
import { B2bCompanySelectInput } from '@sh4res/bo-common/fields';
import { useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type BulkTemplateRequestBody =
  | PostB2bWithdrawalBulkTemplateEndpoint['Client']['RequestBody']
  | PostBoExternalWithdrawalBulkTemplateEndpoint['Client']['RequestBody'];

type BulkTemplateResponseBody =
  | PostB2bWithdrawalBulkTemplateEndpoint['Client']['ResponseBody']
  | PostBoExternalWithdrawalBulkTemplateEndpoint['Client']['ResponseBody'];

const testId = 'BoExternalB2bWithdrawalBulkCreateGeneralInfoStep';

export function GeneralInfoStep() {
  const notify = useNotify();
  const resourceBasePath = useResourceBasePath();
  const [createBulkTemplate, { isLoading: isDownloading }] = useCreatePromise<
    BulkTemplateRequestBody,
    BulkTemplateResponseBody
  >();

  const companyId = useWatch<FormValues, 'b2b_company_id'>({ name: 'b2b_company_id' });
  const instrumentId = useWatch<FormValues, 'instrument_id'>({ name: 'instrument_id' });

  const companyReference = `${resourceBasePath}companies` as const;
  const endpointBase = `${resourceBasePath}withdrawals/bulk` as const;

  const currentDate = new Date();

  const handleDownloadTemplate = async () => {
    if (G.isNullable(companyId) || G.isNullable(instrumentId)) {
      notify(
        'resources.bo_common.b2b.withdrawals.bulk_create.errors.select_company_and_instrument',
        {
          type: 'warning',
        },
      );
      return;
    }

    try {
      const response = await createBulkTemplate(`${endpointBase}/template`, {
        data: {
          b2b_company_id: companyId,
          instrument_id: instrumentId,
        },
      });

      window.open(response.url, '_blank', 'noopener,noreferrer');
      notify('resources.bo_common.b2b.withdrawals.bulk_create.success.template_generated', {
        type: 'info',
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error).toLowerCase();

      if (errorMessage.includes('no_rows')) {
        notify('resources.bo_common.b2b.withdrawals.bulk_create.labels.no_rows_found', {
          type: 'warning',
        });
        return;
      }

      notify('resources.bo_common.b2b.withdrawals.bulk_create.errors.template_generation_failed', {
        type: 'error',
      });
    }
  };

  return (
    <>
      <TypographyTranslated
        variant="subtitle2"
        sx={{ fontWeight: 700 }}
        label="resources.bo_common.b2b.withdrawals.bulk_create.general_info.header_1"
      />
      {!isBoExternal() && (
        <B2bCompanySelectInput
          source="b2b_company_id"
          reference={companyReference}
          referenceFilter={{
            is_international: true,
            has_indirect_management_contract: true,
          }}
          testId={`${testId}_CompanySelectInput`}
        />
      )}
      <ReferenceInput
        source="instrument_id"
        reference="instruments"
        filter={{
          to_redemption_period_start_at: format(addDays(currentDate, 1), 'yyyy-MM-dd'),
          from_redemption_period_end_at: format(
            subDays(currentDate, isBoExternal() ? 1 : 5),
            'yyyy-MM-dd',
          ),
        }}
        perPage={25}
        data-testid={`${testId}_InstrumentReferenceInput`}>
        <AutocompleteInput
          optionText={(instrument: GetInstrumentsResponseItem) =>
            `${instrument.isin ?? instrument.symbol} - ${instrument.name ?? instrument.short_name ?? ''}`
          }
          filterToQuery={(searchText) => ({
            isin: searchText,
          })}
          label={'resources.bo_common.b2b.withdrawals.bulk_create.labels.instrument'}
          validate={[required()]}
          data-testid={`${testId}_InstrumentAutocompleteInput`}
        />
      </ReferenceInput>
      <TypographyTranslated
        variant="subtitle2"
        sx={{ fontWeight: 700 }}
        label="resources.bo_common.b2b.withdrawals.bulk_create.general_info.header_2"
      />

      <Button
        variant="contained"
        disabled={G.isNullable(companyId) || G.isNullable(instrumentId) || isDownloading}
        label={'resources.bo_common.b2b.withdrawals.bulk_create.labels.download_eligible_employees'}
        permissions={{
          resource: `${endpointBase}/template`,
          action: 'create',
        }}
        onClick={() => {
          void handleDownloadTemplate();
        }}
        data-testid={`${testId}_DownloadTemplateButton`}
      />
      <TypographyTranslated
        variant="subtitle2"
        sx={{ fontWeight: 700 }}
        label="resources.bo_common.b2b.withdrawals.bulk_create.general_info.header_3"
      />

      <TypographyTranslated label="resources.bo_common.b2b.withdrawals.bulk_create.general_info.complete_file_description" />

      <TypographyTranslated
        variant="subtitle2"
        sx={{ fontWeight: 700 }}
        label="resources.bo_common.b2b.withdrawals.bulk_create.general_info.header_4"
      />

      <FileInput
        source="bulk_file"
        accept="application/vnd.ms-excel.sheet.macroEnabled.12,.xlsm"
        validate={[required()]}
        multiple={false}
        label={'resources.bo_common.b2b.withdrawals.bulk_create.labels.upload_filled_template'}
        data-testid={`${testId}_BulkFileInput`}>
        <FileField source="src" title="title" data-testid={`${testId}_BulkFileField`} />
      </FileInput>
    </>
  );
}
